import React, { FC, useState, useEffect } from "react";
import { Checkbox, Loader, Dimmer, Label, Segment, Button, Modal, Icon, Input, Form, Message } from "semantic-ui-react";
import { useApi, api } from "../../api/api";
import { ServerStatus } from "../../api/sftp";
import { SyncButton } from "../../components/buttons";
import { envColor } from "../../common";
import Table, { ActionsCell, TextCell } from "../../components/Table";

interface UserWindowProps {
    title: string;
    onClose: () => void;
    onSave: (user: Record<string, string>) => void;
    editedUser?: Record<string, string>;
}

const UserWindow: FC<UserWindowProps> = ({ title, onClose, onSave, editedUser }) => {
    const [username, setUsername] = useState<string>(editedUser?.user ?? "");
    const [password, setPassword] = useState<string>(editedUser?.password ?? "");
    const [passwordError, setPasswordError] = useState<string>("");
    const [passwordVisible, setPasswordVisible] = useState<boolean>(false);

    const validatePassword = (password: string): boolean => {
        // Password must be at least 8 characters long
        // Include at least one uppercase letter
        // Include at least one lowercase letter
        // Include at least one digit
        // Include at least one special character
        const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
        return regex.test(password);
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newPassword = e.target.value.replace(" ", "");
        setPassword(newPassword);
        if (!validatePassword(newPassword)) {
            setPasswordError(
                "Password must be at least 8 characters long and include uppercase, lowercase letters, numbers, and special characters.",
            );
        } else {
            setPasswordError("");
        }
    };

    const save = () => {
        if (!validatePassword(password)) {
            setPasswordError("Password does not meet complexity requirements.");
            return;
        }
        onSave({ username, password });
    };

    return (
        <Modal size="small" closeIcon open={true} onClose={onClose}>
            <Modal.Header>{title}</Modal.Header>
            <Modal.Content scrolling>
                <Form>
                    <Form.Field>
                        <Input
                            label="Username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value.replace(" ", ""))}
                        />
                    </Form.Field>
                    <Form.Field error={!!passwordError}>
                        <Input
                            label="Password"
                            type={passwordVisible ? "text" : "password"}
                            value={password}
                            onChange={handlePasswordChange}
                            action={{
                                icon: passwordVisible ? "eye slash" : "eye",
                                onClick: () => setPasswordVisible(!passwordVisible),
                            }}
                        />
                        {passwordError && <Message negative content={passwordError} />}
                    </Form.Field>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    circular
                    icon
                    labelPosition="left"
                    color={envColor}
                    onClick={save}
                    disabled={!!passwordError || !password}
                >
                    <Icon name="save" />
                    Save
                </Button>
                <Button circular onClick={onClose}>
                    Close
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

const SFTPSettings: FC<{}> = () => {
    const sftpApi = useApi(api.sftp);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState<ServerStatus>();
    const [enabled, setEnabled] = useState(false);
    const [users, setUsers] = useState<Record<string, string>>({});
    const [userWindowVisibility, setUserWindowVisibility] = useState(false);
    const [dlgMode, setDlgMode] = useState<"new" | "edit">();
    const [editedUser, setEditedUser] = useState(undefined);

    const toggleServer = () => {
        enabled ? sftpApi.stop() : sftpApi.start();

        load(2000);
    };

    const loadStatus = async () => {
        const newStatus = await sftpApi.status();
        setStatus(newStatus);
        setEnabled(["ONLINE", "STARTING"].includes(newStatus.state));
    };

    const load = async (waitMs: number = 0) => {
        setLoading(true);

        if (waitMs > 0) {
            await new Promise((r) => setTimeout(r, waitMs));
        }

        await loadStatus();
        const newUsers = await sftpApi.list_users();
        setUsers(newUsers);

        setLoading(false);
    };

    useEffect(() => {
        load();
    }, []);

    const statusColor = (() => {
        switch (status?.state) {
            case "ONLINE":
                return "green";
            case "OFFLINE":
                return "red";
            default:
                return "yellow";
        }
    })();

    const userRows = Object.keys(users).map((user) => {
        return { user: user, password: users[user] };
    });

    const newUserDlg = () => {
        setEditedUser(undefined);
        setDlgMode("new");
        setUserWindowVisibility(true);
    };

    const editUserDlg = (user: any) => {
        setEditedUser(user);
        setDlgMode("edit");
        setUserWindowVisibility(true);
    };

    const closeDlg = () => {
        setUserWindowVisibility(false);
    };

    const saveUser = (user: Record<string, string>) => {
        if (dlgMode === "edit") {
            sftpApi.edit_user(user.username, user.password);
        } else if (dlgMode === "new") {
            sftpApi.create_user(user.username, user.password);
        }

        closeDlg();
        load(1000);
    };

    const deleteUser = (user: any) => {
        sftpApi.delete_user(user.user);
        load(1000);
    };

    const userActions = [
        { icon: "delete", onClick: deleteUser },
        { icon: "edit", onClick: editUserDlg },
    ];

    return (
        <>
            <Dimmer inverted active={loading}>
                <Loader />
            </Dimmer>
            <Checkbox toggle onClick={toggleServer} checked={enabled} />

            {userWindowVisibility && (
                <UserWindow title={`${dlgMode} user`} onSave={saveUser} onClose={closeDlg} editedUser={editedUser} />
            )}

            <div style={{ margin: "20px" }}>
                <SyncButton color={envColor} load={load} />
                <Label color={statusColor}>Status : {status?.state ?? "PENDING"}</Label>
                <Label basic>{status?.host}</Label>
            </div>

            <Segment>
                <Button onClick={newUserDlg}>Add user</Button>
                {userRows.length === 0 ? (
                    "No users"
                ) : (
                    <Table rows={userRows} pageSize={10}>
                        <TextCell
                            header="User"
                            value={(r) => {
                                return r.user;
                            }}
                        />
                        <TextCell header="Password" value={(r) => "*****"} />
                        <ActionsCell header="" actions={userActions} />
                    </Table>
                )}
            </Segment>
        </>
    );
};

export default SFTPSettings;
